import { observer } from "mobx-react";
import { useState } from "react";
import { RevokeAppModal } from "@/components/oauth/revoke-app-modal";
import { absoluteDate, relativeDate } from "@/lib/oauth-date";
import { oauthIdpService as oauthIdp, useMyOAuthConsents } from "@/hooks/use-oauth-apps";
import { type OAuthConsent } from "@/services/oauth-idp.service";
import { useTranslation } from "@/i18n";

/**
 * Profile settings → Connected apps.
 *
 * Read+revoke view of every active OAuth grant the current user has
 * made, grouped by workspace. Revoke kills the OAuthConsent server-side;
 * already-issued tokens still pass introspection until they expire.
 */
export const ProfileConnectedAppsPage = observer(function ProfileConnectedAppsPage() {
  const { t } = useTranslation();
  // The user's grants from the shared cache (NG-2814); a failed read is logged
  // by the cache.
  const { consents: loaded, error: loadError, dropConsent } = useMyOAuthConsents();
  const consents = loaded ?? null;
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const error =
    revokeError ??
    (loadError ? (loadError.message ?? t("profile_settings.connected_apps.load_error")) : null);
  const [pendingRevoke, setPendingRevoke] = useState<OAuthConsent | null>(null);
  const [revoking, setRevoking] = useState(false);

  async function handleRevoke() {
    if (!pendingRevoke) return;
    setRevoking(true);
    try {
      await oauthIdp.revokeConsent(pendingRevoke.id);
      dropConsent(pendingRevoke.id);
      setPendingRevoke(null);
    } catch (err: any) {
      console.error("connected apps: failed to revoke a consent", {
        consentId: pendingRevoke.id,
        err,
      });
      setRevokeError(err?.message ?? t("profile_settings.connected_apps.revoke_error"));
    } finally {
      setRevoking(false);
    }
  }

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (consents === null)
    return (
      <p className="text-sm text-muted-foreground">
        {t("profile_settings.connected_apps.loading")}
      </p>
    );
  if (consents.length === 0) {
    return (
      <div>
        <h2 className="text-lg font-semibold">{t("profile_settings.tabs.connected_apps")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("profile_settings.connected_apps.empty")}
        </p>
      </div>
    );
  }

  // Group by workspace
  const grouped = new Map<string, OAuthConsent[]>();
  for (const c of consents) {
    const key = `${c.workspace_slug}|${c.workspace_name}`;
    grouped.set(key, [...(grouped.get(key) ?? []), c]);
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-lg font-semibold">{t("profile_settings.tabs.connected_apps")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("profile_settings.connected_apps.intro")}
        </p>
      </header>

      {[...grouped.entries()].map(([key, group]) => {
        const [, workspaceName] = key.split("|");
        return (
          <section key={key}>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              {workspaceName}
            </h3>
            <ul className="divide-y divide-border rounded-sm border border-border">
              {group.map((c) => (
                <li key={c.id} className="flex items-start justify-between gap-4 p-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-medium">
                      <span>{c.app_name}</span>
                      {c.is_expired ? (
                        <span
                          className="rounded-sm bg-warning/15 px-1.5 py-0.5 text-[10px] font-medium text-warning"
                          title={t("profile_settings.connected_apps.expired_title")}
                        >
                          {t("profile_settings.connected_apps.expired")}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {c.scopes.map((s) => (
                        <span
                          key={s}
                          className="rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[10px]"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {t("profile_settings.connected_apps.granted")}{" "}
                      <span title={absoluteDate(c.granted_at)}>{absoluteDate(c.granted_at)}</span>
                      {c.last_used_at ? (
                        <>
                          {` · ${t("profile_settings.connected_apps.last_used")} `}
                          <span title={absoluteDate(c.last_used_at)}>
                            {relativeDate(c.last_used_at)}
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPendingRevoke(c)}
                    className="text-xs text-destructive underline"
                  >
                    {t("profile_settings.connected_apps.revoke")}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      <RevokeAppModal
        appName={pendingRevoke?.app_name ?? ""}
        isOpen={pendingRevoke !== null}
        isSubmitting={revoking}
        onCancel={() => setPendingRevoke(null)}
        onConfirm={handleRevoke}
      />
    </div>
  );
});
